import { useEffect, useRef } from "react";
import { TBasicAlertProps } from "../components/common/BasicAlert/BasicAlert";
import Config from "../config/config";

type TFlashMessageIdValues = {
  "alert-login-adfs": {
    type: TBasicAlertProps["type"];
    message: string;
  }
}

type TFlashMessageIdKeys = keyof TFlashMessageIdValues;

const PREFIX = `${Config.LOCAL_STORAGE_NAME}_fms_`;

/**
 * @param id Identifier of the flash storage
 * @param mode Whether to persist between tab or session
 */
export default function useFlashMessageStore<T extends TFlashMessageIdKeys>(
  id: T,
  callback?: (message: TFlashMessageIdValues[T]) => void,
  mode?: "local" | "session"
) {
  const storage = useRef(mode ?? "session");
  const storeGet = () => {
    let read: any;
    let key = PREFIX + id;

    if (storage.current === "session") {
      read = sessionStorage.getItem(key);
      sessionStorage.removeItem(key);
    } else {
      read = localStorage.getItem(key);
      localStorage.removeItem(key);
    }

    try {
      let parsed = JSON.parse(read);

      if (parsed?.type === "fms") {
        return parsed?.data;
      }
    } catch (ignore) {}

    return;
  };
  const storeSet = (message: TFlashMessageIdValues[T]) => {
    if (storage.current === "session") {
      sessionStorage.setItem(PREFIX + id, JSON.stringify({ type: "fms", data: message }));
    } else {
      localStorage.setItem(PREFIX + id, JSON.stringify({ type: "fms", data: message }));
    }
  };
  const setMessage = (data: TFlashMessageIdValues[T]) => {
    // we need to store if only callback is undefined
    if (typeof callback === "undefined") {
      storeSet(data);
    } else {
      callback(data);
    }
  };

  useEffect(() => {
    if (typeof callback === "function") {
      const val = storeGet();

      if (typeof val !== "undefined") {
        callback(val);
      }
    }
  }, []);

  return {
    setMessage
  };
}
